import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { getAoMasterData, getQuote } from "../../features/product";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../common/loader";
import { useTranslation } from "react-i18next";
import { formatToDDMMYYYY } from "../../utils/commonHelper";
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import SelectDropdown from "../SelectDropdown";
import * as Yup from "yup";

const ageCategories = [
  { key: "no_of_adults_-_(age_18_or_above)", label: "Adults (19 to 70)" },
  { key: "no_of_child_-_(age_16_or_below)" || "no_of_child_-_(age_17_to_18)", label: "Children (0 to 18)" },
  { key: "no_of_seniors_-_(age_71_to_79)", label: "Seniors (71 to 79)" },
];

const getAgeGroupRanges = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const currentDay = new Date().getDate();

  const toDateString = (year, endOfYear = false) =>
    `${year}-${endOfYear ? `${String(currentMonth + 1).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}` : "01-01"}`;

  const getYearYearsAgo = (years) => currentYear - years;
  return {
    "no_of_adults_-_(age_18_or_above)": {
      min: toDateString(getYearYearsAgo(70)),
      max: toDateString(getYearYearsAgo(19), true),
    },
    "no_of_child_-_(age_16_or_below)": {
      min: toDateString(getYearYearsAgo(18)),
      max: toDateString(getYearYearsAgo(0), true),
    },
    "no_of_seniors_-_(age_71_to_79)": {
      min: toDateString(getYearYearsAgo(79)),
      max: toDateString(getYearYearsAgo(71), true),
    },
  };
};

// validation for each person
const PersonSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  dOB: Yup.string().required("Birth Date is required"),
  passportNo: Yup.string().required("Passport Number is required"),
  email: Yup.string()
    .email("Invalid email")
    .when("isPrincipal", {
      is: "Y",
      then: (schema) => schema.required("Email is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  remarks: Yup.string().when("isPrincipal", {
    is: "Y",
    then: (schema) => schema.required("Remarks are required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const AoPersonDetailsForm = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const productDataStr = localStorage.getItem('product_inquiry');
  const productData = JSON.parse(productDataStr);
  const prefilledForms = JSON.parse(productData.form_data);
  const emailIds = prefilledForms.email;

  const prefillCounts = useMemo(
    () => location?.state?.prefillCounts || {},
    [location?.state]
  );

  const [formValues, setFormValues] = React.useState({
    counts: prefillCounts,
    people: [],
  });

  useEffect(() => {
    handleCountChange('', '', formValues, '');
  }, [prefillCounts]);

  const planData = useMemo(
    () => location?.state?.data || {},
    [location?.state]
  );
  const { quoteLoader, aoMasterData } = useSelector((state) => state.product);

  // when count changes → regenerate people
  const handleCountChange = (e, key, values, setValues) => {
    const value = parseInt(e?.target?.value) || 0;
    const newCounts = { ...values.counts, [key]: value };

    const ranges = getAgeGroupRanges();
    const newPeople = [];

    Object.entries(newCounts).forEach(([categoryKey, count]) => {
      let flag = "";
      if (categoryKey.includes("adults")) flag = "A";
      else if (categoryKey.includes("child")) flag = "C";
      else if (categoryKey.includes("seniors")) flag = "S";

      for (let i = 0; i < count; i++) {
        newPeople.push({
          rowId: i + 1,
          flag,
          title: "",
          lastName: "",
          firstName: "",
          dOB: "",
          passportNo: "",
          isPrincipal: newPeople.length === 0 ? "Y" : "N",
          email: i == 0 ? emailIds : '',
          remarks: "",
          dateLimits: ranges[categoryKey],
          groupKey: categoryKey,
        });
      }
    });
    // setValues({ ...values, counts: newCounts, people: newPeople });
    setFormValues({ ...values, counts: newCounts, people: newPeople });
  };

  const handlePersonChange = (setFieldValue, index, field, value) => {
    if (field === "gender") {
      setFieldValue(
        `people.${index}.gender`,
        value === "M"
          ? { code: "M", value: "Male" }
          : { code: "F", value: "Female" }
      );
    } else {
      setFieldValue(`people.${index}.${field}`, value);
    }
  };

  useEffect(() => {
    dispatch(
      getAoMasterData({
        launguage: currentLanguage === "ar" ? "AR" : "ENG",
        typeCode: "01",
      })
    );
  }, []);

  if (quoteLoader) return <Loader type="logo-gif" />;

  return (
    <div className="container py-4">
      <Formik
        enableReinitialize
        initialValues={formValues}
        validationSchema={Yup.object({
          counts: Yup.object(),
          people: Yup.array().of(PersonSchema),
        })}
        onSubmit={(values, { setSubmitting }) => {
          const selected_covers = planData?.covers
            ?.map((cover) =>
              cover.isSelected
                ? {
                  code: cover.code,
                  cvrType: cover.cvrType,
                  premium: cover.premium,
                }
                : null
            )
            .filter((item) => item !== null);

          const passengerInfo = values.people.map((person) => ({
            ...person,
            dOB: formatToDDMMYYYY(person.dOB),
          }));

          const remarks = passengerInfo[0]?.remarks;

          dispatch(
            getQuote({
              ...planData,
              ...planData?.extracted_data,
              selected_covers,
              passengerInfo,
              insuredName:
                passengerInfo[0]?.firstName + " " + passengerInfo[0]?.lastName,
              remarks,
            })
          ).then((res) => {
            navigate("/checkout", { state: { existingData: res.payload } });
          });

          setSubmitting(false);
        }}
      >
        {({ values, setValues, setFieldValue }) => (
          <>
          <Form>
            <h3 className="mb-4">Enter Number of People by Age Group</h3>
            <div className="row">
              {ageCategories.map(({ key, label }) => (
                <div className="col-md-4 mb-3" key={key}>
                  <label className="form-label">{label}</label>
                  <input
                    type="number"
                    className="form-control element-style"
                    min="0"
                    value={values.counts[key] || ""}
                    onChange={(e) =>
                      handleCountChange(e, key, values, setValues)
                    }
                  />
                </div>
              ))}
            </div>

            {values.people.length > 0 && <hr className="my-4" />}

            {values.people.map((person, index) => (
              <div className="card mb-4" key={index}>
                <div className="card-header">
                  <strong>
                    Person {index + 1} -{" "}
                    {
                      ageCategories.find((a) => a.key === person.groupKey)
                        ?.label
                    }
                  </strong>
                </div>
                <div className="card-body row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Title</label>
                    <SelectDropdown
                      name="title"
                      placeholder="Select Title"
                      options={aoMasterData?.map((item) => ({
                        label: item.text,
                        value: item.code,
                      })) || []}
                      defaultValue={person.title ? { value: person.title } : null}
                      handleOnChange={(name, value) =>
                        handlePersonChange(setFieldValue, index, "title", value)
                      }
                      isSearchable={false}
                    />
                    <ErrorMessage
                      name={`people.${index}.title`}
                      component="div"
                      className="text-danger"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      className="form-control element-style"
                      value={person.firstName}
                      onChange={(e) =>
                        handlePersonChange(
                          setFieldValue,
                          index,
                          "firstName",
                          e.target.value
                        )
                      }
                    />
                    <ErrorMessage
                      name={`people.${index}.firstName`}
                      component="div"
                      className="text-danger"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-control element-style"
                      value={person.lastName}
                      onChange={(e) =>
                        handlePersonChange(
                          setFieldValue,
                          index,
                          "lastName",
                          e.target.value
                        )
                      }
                    />
                    <ErrorMessage
                      name={`people.${index}.lastName`}
                      component="div"
                      className="text-danger"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Birth Date</label>
                    <input
                      type="date"
                      className="form-control element-style"
                      value={person.dOB}
                      min={person?.dateLimits?.min}
                      max={person?.dateLimits?.max}
                      onChange={(e) =>
                        handlePersonChange(
                          setFieldValue,
                          index,
                          "dOB",
                          e.target.value
                        )
                      }
                    />
                    <small className="text-muted">
                      {" "}
                      Allowed: {person?.dateLimits?.min} to{" "}
                      {person?.dateLimits?.max}{" "}
                    </small>
                    <ErrorMessage
                      name={`people.${index}.dOB`}
                      component="div"
                      className="text-danger"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Passport No</label>
                    <input
                      type="text"
                      className="form-control element-style"
                      value={person.passportNo}
                      onChange={(e) =>
                        handlePersonChange(
                          setFieldValue,
                          index,
                          "passportNo",
                          e.target.value
                        )
                      }
                    />
                    <ErrorMessage
                      name={`people.${index}.passportNo`}
                      component="div"
                      className="text-danger"
                    />
                  </div>

                  {person.isPrincipal === "Y" && (
                    <>
                      <div className="col-md-4">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control element-style"
                          value={person.email}
                          onChange={(e) =>
                            handlePersonChange(
                              setFieldValue,
                              index,
                              "email",
                              e.target.value
                            )
                          }
                        />
                        <ErrorMessage
                          name={`people.${index}.email`}
                          component="div"
                          className="text-danger"
                        />
                      </div>
                      <div className="col-md-12">
                        <label className="form-label">Remarks</label>
                        <textarea
                          className="form-control element-style"
                          value={person.remarks}
                          onChange={(e) =>
                            handlePersonChange(
                              setFieldValue,
                              index,
                              "remarks",
                              e.target.value
                            )
                          }
                        />
                        <ErrorMessage
                          name={`people.${index}.remarks`}
                          component="div"
                          className="text-danger"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}

            {values.people.length > 0 && (
              <div className="text-end">
                <Button type="submit" className="h-btn">
                  Submit
                </Button>
              </div>
            )}
          </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default AoPersonDetailsForm;
